import { customerSuccessCopy } from '../internal/customerSuccessCopy.js';
import { metadataDefaults } from '../internal/metadataDefaults.js';
import { resources } from '../internal/resources.js';
export const EXPERIENCE_DEFAULTS_VERSION = 5;
export const experienceLocales = Object.keys(resources).sort();
const studyOwnedTextKeys = new Set([
    'landing.title',
    'complete.participantEmail.title',
    'complete.participantEmail.description',
]);
export const experienceTextKeys = [
    ...Object.keys(resources.en),
    'metadata.invitationTitle',
    'metadata.brandedInvitationTitle',
    'metadata.fallbackDescription',
]
    .filter((key) => !studyOwnedTextKeys.has(key))
    .sort();
export function normalizeExperienceLocale(locale) {
    const normalized = Intl.getCanonicalLocales(locale)[0];
    return normalized === 'nb' ? 'no' : normalized.replace(/^nb-/, 'no-');
}
export function getExperienceDefaults(productLine, locale) {
    const normalized = normalizeExperienceLocale(locale);
    const base = normalized.split('-')[0];
    const copy = { ...resources.en, ...resources[base], ...resources[normalized] };
    // Existing check-in-specific keys remain compatible with current participant screens.
    if (productLine === 'customer_success') {
        for (const [target, source] of Object.entries({
            'landing.description': 'landing.checkInDescription',
            'preflight.subtitle': 'preflight.checkInSubtitle',
            'preflight.startInterview': 'preflight.startCheckIn',
            'complete.title': 'complete.checkInTitle',
            'studyClosed.title': 'studyClosed.checkInTitle',
            'studyClosed.message': 'studyClosed.checkInMessage',
        }))
            if (copy[source])
                copy[target] = copy[source];
    }
    const metadata = metadataDefaults[base] ?? metadataDefaults.en;
    const title = metadata[productLine === 'customer_success' ? 2 : 0];
    copy['metadata.invitationTitle'] =
        productLine === 'customer_success' ? title : (copy['landing.title'] ?? title);
    copy['metadata.brandedInvitationTitle'] = `{{brandName}} - ${title}`;
    copy['metadata.fallbackDescription'] = metadata[productLine === 'customer_success' ? 3 : 1];
    if (productLine === 'customer_success') {
        const productCopy = { ...customerSuccessCopy.en, ...customerSuccessCopy[base] };
        for (const key of Object.keys(copy)) {
            if (!productCopy[key])
                continue;
            // Generic privacy keys historically have no vendor placeholder. Keep their
            // contract stable; check-in privacy keys carry the named vendor.
            const isGenericPrivacy = /^preflight\.(microphone|terms)\.privacy/.test(key);
            copy[key] = isGenericPrivacy
                ? productCopy[key].replaceAll('{{vendor}}', copy['checkIn.vendorFallback'])
                : productCopy[key];
        }
    }
    return copy;
}
export function resolveExperienceCopy(productLine, locale, overrides = {}) {
    const normalized = normalizeExperienceLocale(locale);
    const base = normalized.split('-')[0];
    const values = overrides[productLine];
    const copy = {
        ...getExperienceDefaults(productLine, normalized),
        ...values?.[base],
        ...values?.[normalized],
    };
    return Object.fromEntries(experienceTextKeys.map((key) => [key, copy[key]]));
}
export function validateExperienceOverrides(overrides) {
    for (const [line, locales] of Object.entries(overrides)) {
        if (line !== 'insights' && line !== 'customer_success')
            throw new Error('Unknown product line');
        if (Object.keys(locales ?? {}).length > 50)
            throw new Error('A product line can have at most 50 locales');
        for (const [locale, texts] of Object.entries(locales ?? {})) {
            if (normalizeExperienceLocale(locale) !== locale)
                throw new Error(`Use normalized locale ${normalizeExperienceLocale(locale)}`);
            const defaults = getExperienceDefaults(line, locale);
            for (const [key, value] of Object.entries(texts)) {
                if (!experienceTextKeys.includes(key))
                    throw new Error(`Text is not editable: ${key}`);
                if (!value.trim() || value.length > 4000 || /<[^>]*>/.test(value))
                    throw new Error(`Invalid plain text: ${key}`);
                const tokens = (text) => [...text.matchAll(/{{\s*([\w]+)\s*}}/g)]
                    .map((m) => m[1])
                    .sort()
                    .join(',');
                if (/[{}]/.test(value.replace(/{{\s*([\w]+)\s*}}/g, '')) ||
                    tokens(value) !== tokens(defaults[key]))
                    throw new Error(`Preserve placeholders for ${key}`);
            }
        }
    }
}
//# sourceMappingURL=resolveExperienceCopy.js.map